import { useRouter } from '@ucheba/utils/hooks/useRouter'
import { useCallback, useEffect, useMemo } from 'react'
import { stringifyUrl } from '@ucheba/utils/helpers/router'
import { EDataKeys } from '@ucheba/utils/types/dataKeys'
import { useSelector } from 'react-redux'
import { updateSticky } from '@ucheba/ui/components/Sticky'
import { coreQuery } from '@ucheba/utils/constants/queries'
import { eventsSearchSelectors } from '@ucheba/store/events/search'
import { useDebouncedCallback } from 'use-debounce'
import { EEventsDataKeys } from '@ucheba/store/events/search/types'
import { allTagName } from '@ucheba/store/specialities/search/constants'
import { IFacet } from '@ucheba/utils/types/search'
import { locationSelectors } from '@ucheba/store/location'
import dayjs from '@ucheba/utils/helpers/date/dayjs'
import {
  eventsFacetBySearchQuery,
  searchQueryByEventsFacet,
  sectionsEventsNavigationItems,
  slugs,
} from './constants'
import {
  EEventsQueryParams,
  IUseEventsPageHeaderCore,
  IUseEventsPageSelectedFacetsCore,
  IUseEventsSearchPageFacetsCore,
  IUseTitleWithQuery,
  TUseEventsPageSearchSelectedFacetsItem,
} from './types'

export const useEventsPageHeaderCore: IUseEventsPageHeaderCore = () => {
  const router = useRouter()
  const title = useMemo(() => 'Дни открытых дверей', [])

  /* Формируем список навигации */
  const items = useMemo(() => {
    return sectionsEventsNavigationItems.map((section, index, array) => {
      const cours = array.find((el) => Array.isArray(el.slug))?.slug || []
      const data = Array.isArray(section.slug) ? [...section.slug] : [section.slug]

      const preparedSlug =
        section.slug === sectionsEventsNavigationItems[0].slug
          ? array
              .filter((el, key) => index !== key && !Array.isArray(el.slug))
              .map(({ slug }) => slug)
              .concat(cours)
          : data

      return {
        ...section,
        href: stringifyUrl(router.asPath, {
          set: {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            [EDataKeys.programTypes]: preparedSlug,
          },
        }),
      }
    })
  }, [router.asPath])

  /** Получаем индекс активного пункта в зависимости от квери параметров */
  const activeIndex = useMemo(() => {
    const activeSection = router.query[EDataKeys.programTypes]

    return activeSection && activeSection.length < slugs.length
      ? sectionsEventsNavigationItems.findIndex(({ slug }) => {
          if (Array.isArray(slug) && Array.isArray(activeSection)) {
            return slug.every((el, index) => el === activeSection[index])
          }

          return slug === String(activeSection)
        })
      : 0
  }, [router.query])

  return {
    title,
    items,
    activeIndex,
  }
}

let resultQuery
const excludeQuery: Record<string, any>[] = []

export const useEventsPageSelectedFacets: IUseEventsPageSelectedFacetsCore = () => {
  const entitySearch = useSelector(eventsSearchSelectors.entity)
  const facets = entitySearch?.facets
  const router = useRouter()
  const { query } = router

  /** По квери параметрам из фасетов получаем полные данные о выбранных фасетах */
  const selectedFacets = useMemo(() => {
    if (!facets) return []

    const resultSelectedFacets: TUseEventsPageSearchSelectedFacetsItem[] = []

    Object.keys(query).forEach((queryKey) => {
      const facetName = eventsFacetBySearchQuery[queryKey]

      if (!facets[facetName]) return

      const targetFacets = facets[facetName]
        .reduce((acc, facetItem) => {
          if (facetItem.items) {
            facetItem.items.forEach((item) => {
              let newItem = item

              if (item.name === allTagName) {
                newItem = { ...item }
                newItem.name = facetItem.name
              }

              acc.push(newItem)
            })
          } else {
            acc.push(facetItem)
          }

          return acc
        }, [] as IFacet[])
        .filter((targetFacetItem) => {
          const targetValue = String(targetFacetItem.value)
          const queryValue = query[queryKey]

          return (
            (Array.isArray(queryValue) && queryValue.includes(targetValue)) ||
            targetValue === queryValue
          )
        })
        .map((targetFacetsItem) => ({
          ...targetFacetsItem,
          value: String(targetFacetsItem.value),
          queryKey,
        }))

      targetFacets.forEach((targetFacetsItem) =>
        resultSelectedFacets.push(targetFacetsItem)
      )
    })

    return resultSelectedFacets
  }, [facets, query])

  const updateRouter = useDebouncedCallback(() => {
    router.push(
      stringifyUrl(router.asPath, {
        exclude: [...excludeQuery, coreQuery.page],
      })
    )
  }, 500)

  /** Обновляет роут без удаленного фасета */
  const removeFacet = useCallback(
    (queryKey, id) => {
      excludeQuery.push({ [queryKey]: id })

      updateRouter()
    },
    [updateRouter]
  )

  return {
    selectedFacets,
    removeFacet,
  }
}

export const useEventsSearchPageFacetsCore: IUseEventsSearchPageFacetsCore = (props) => {
  const { durationDebounced = 100 } = props || {}
  const entitySearch = useSelector(eventsSearchSelectors.entity)
  const facets = entitySearch?.facets
  const router = useRouter()

  /* Обновляем значение Стики, когда переход на поиск с новыми квери параметрами заканчивается */
  useEffect(() => {
    const routerHandle = (): void => {
      updateSticky()
    }

    router.events.on('routeChangeComplete', routerHandle)

    return () => router.events.off('routeChangeComplete', routerHandle)
  }, [router.events])

  /* Обновляем роутер в зависимости от выбранных фасетов */
  const updateRouter = useDebouncedCallback((params?: Record<string, any>) => {
    router.push(
      stringifyUrl(router.asPath, {
        update: params || resultQuery,
        exclude: [coreQuery.page, EEventsDataKeys.query],
      }),
      undefined,
      {
        scroll: false,
      }
    )

    resultQuery = {}
  }, durationDebounced)

  /** При сабмите фасета, обновляем роут */
  const onSubmitFunction = useCallback(
    async (checked, facetName) => {
      resultQuery = {
        ...resultQuery,
        [searchQueryByEventsFacet[facetName]]: checked,
      }

      updateRouter()
    },
    [updateRouter]
  )

  const onSubmit = (facetName) => (checked) => onSubmitFunction(checked, facetName)

  return {
    facets,
    onSubmit,
    updateRouter,
  }
}

/* Хук для получения данных для seo дней открытых дверей */
export const useEventsSeoData: IUseTitleWithQuery = (query) => {
  const locationById = useSelector(locationSelectors.locationByLocationIds)

  const programTypesString = useMemo(() => {
    return 'курсы, МВА, второе высшее, магистратура, аспирантура'
  }, [query])

  /* Получаю строку 'онлайн' или 'офлайн' если в query есть formats или есть только один formats, иначе "" */
  const format = useMemo(() => {
    // eslint-disable-next-line no-nested-ternary
    return query[EEventsQueryParams.formatsIds] &&
      query[EEventsQueryParams.formatsIds].length === 1
      ? query[EEventsQueryParams.formatsIds][0] === 'online'
        ? ' онлайн'
        : ' офлайн'
      : ''
  }, [query[EEventsQueryParams.formatsIds]])

  /* Получаю строку города если в query есть один выбранный locationIds, иначе "" */
  const location = useMemo(() => {
    if (!locationById || !locationById.namePrepositional) {
      return ''
    }

    return ` в ${locationById?.namePrepositional}`
  }, [query[EEventsQueryParams.locationIds], locationById])

  /* возвращаю текущий/следующий год */
  const years = useMemo(() => {
    const yearNow = dayjs().format('YYYY')
    const nextYear = Number(yearNow) + 1

    return `в ${yearNow}/${nextYear} году`
  }, [])

  /* возвращаем строку для заголовка в зависиомсти от параметров урла "[в вузах] [онлайн] [в Москве]" */
  const titleWithQuery = useMemo(() => {
    return `${programTypesString}${location}${format}`
  }, [programTypesString, format, location])

  /* возвращаем строку для описания в зависиомсти от параметров урла "[в вузах] [онлайн] [в Москве]" */
  const descriptionWithQuery = useMemo(() => {
    return `Актуальное расписание дней открытых дверей: ${programTypesString}${format}${location} ${years} — выбирай, регистрируйся, приходи и знакомься.`
  }, [programTypesString, format, location])

  /* возвращаем true , в случае если в GET-параметрах не указаны параметры совсем или указано
  не более 1 значения у параметров из списка: formats, locationIds */
  const isIndexAndFollow = useMemo(() => {
    /* перебераем объект query и возвращаем массив булевых выражений, в зависимости от условий */
    const boolArray = Object.keys(query).map((key) => {
      if (
        key === EEventsQueryParams.formatsIds ||
        key === EEventsQueryParams.locationIds ||
        key === EEventsQueryParams.programTypes
      ) {
        if (!query[key]) {
          return true
        }

        if (query[key].length === 1) {
          return true
        }

        return key === EEventsQueryParams.programTypes
      }

      return false
    })

    // если в массиве булевых выражений есть хоть один false то возвращаем false, иначе true
    return boolArray.every((bool) => bool)
  }, [query])

  return {
    titleWithQuery,
    descriptionWithQuery,
    years,
    isIndexAndFollow,
  }
}
